/**
 * HTML -> markdown: the return leg of the WYSIWYG editors.
 *
 * The editors seed their `contentEditable` with `marked.parse(markdown)` and the stored
 * format stays markdown, so everything the toolbar leaves in the DOM has to be
 * serialized back. Reading `innerText` instead -- which is what this replaces -- returned
 * flat text, so a single keystroke anywhere dropped `##`, `**`, `*` and the bullet
 * dashes from the whole document, and cost the block separation markdown needs (one
 * `\n` between `<li>`s, where markdown wants a blank line) so the text came back
 * "todo junto" on reopening.
 *
 * A single module-scope instance on purpose: this runs on EVERY keystroke, and building
 * a `TurndownService` per call would rebuild its whole rule set each time.
 */

import TurndownService from 'turndown';

// Tuned to the subset the toolbar can produce -- the defaults would emit setext
// headings, `_cursiva_`, `* viñeta` and `* * *`, none of which match how the rest of
// the app writes markdown.
const turndownService = new TurndownService({
  headingStyle: 'atx',
  hr: '---',
  bulletListMarker: '-',
  emDelimiter: '*',
  strongDelimiter: '**',
});

// Emphasis carried as an inline style instead of a tag. The toolbar never produces
// this -- Chromium's `execCommand('bold')` leaves `<b>` -- but paste does: Word and
// most web pages style a `<span>` rather than mark it up. Turndown's default rules
// unwrap an unknown `<span>`, so without these the auditor keeps the words and
// silently loses the emphasis on text they pasted in.
const BOLD_STYLE = /(?:^|;)\s*font-weight\s*:\s*(?:bold(?:er)?|[6-9]00)\s*(?:;|$)/i;
const ITALIC_STYLE = /(?:^|;)\s*font-style\s*:\s*(?:italic|oblique)\s*(?:;|$)/i;

turndownService.addRule('styledEmphasis', {
  filter: (node) =>
    node.nodeName === 'SPAN' &&
    (BOLD_STYLE.test(node.getAttribute('style') || '') ||
      ITALIC_STYLE.test(node.getAttribute('style') || '')),
  replacement: (content, node) => {
    // An empty span must not leave orphan `****` behind.
    if (!content.trim()) return '';

    const style = (node as HTMLElement).getAttribute('style') || '';
    // Markdown will not render `** texto **`, so the surrounding whitespace has to
    // stay outside the delimiters.
    const [, leading, core, trailing] = /^(\s*)([\s\S]*?)(\s*)$/.exec(content)!;

    let emphasized = core;
    if (ITALIC_STYLE.test(style)) emphasized = `*${emphasized}*`;
    if (BOLD_STYLE.test(style)) emphasized = `**${emphasized}**`;
    return `${leading}${emphasized}${trailing}`;
  },
});

export function htmlToMarkdown(html: string): string {
  return turndownService.turndown(html || '');
}
